<script setup>
import { ref, computed, h } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { messageStamp } from 'shared/helpers/timeHelper';
import ReportsAPI from 'dashboard/api/reports';
import {
  downloadBlob,
  generateFileName,
} from 'dashboard/helper/downloadHelper';

import Table from 'dashboard/components/table/Table.vue';
import Pagination from 'dashboard/components/table/Pagination.vue';
import V4Button from 'dashboard/components-next/button/Button.vue';
import ReportHeader from './components/ReportHeader.vue';
import ReportFilterSelector from './components/FilterSelector.vue';
import ConversationCell from './components/ConversationCell.vue';

import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';

const { t } = useI18n();

const pageIndex = ref(0);
const from = ref(0);
const to = ref(0);
const inbox = ref(null);
const label = ref(null);
const status = ref(null);
const scopeOptions = computed(() => [
  { value: 'created', label: t('CONVERSATION_TRACKING_REPORTS.SCOPE.CREATED') },
  {
    value: 'returning',
    label: t('CONVERSATION_TRACKING_REPORTS.SCOPE.RETURNING'),
  },
  { value: 'all', label: t('CONVERSATION_TRACKING_REPORTS.SCOPE.ALL') },
]);
const selectedScope = ref(scopeOptions.value[0]);
const conversations = ref([]);
const totalCount = ref(0);
const isLoading = ref(false);

const requestPayload = computed(() => ({
  from: from.value,
  to: to.value,
  inboxId: inbox.value,
  label: label.value,
  status: status.value,
  scope: selectedScope.value.value,
}));

const fetchConversations = async () => {
  isLoading.value = true;
  try {
    const { data } = await ReportsAPI.getConversationTrackingReports({
      ...requestPayload.value,
      page: pageIndex.value + 1,
    });
    conversations.value = data.payload;
    totalCount.value = data.meta.total_count;
  } catch {
    useAlert(t('REPORT.DATA_FETCHING_FAILED'));
  } finally {
    isLoading.value = false;
  }
};

const onFilterChange = ({
  from: fromValue,
  to: toValue,
  selectedInbox,
  selectedLabel,
  selectedStatus,
}) => {
  from.value = fromValue;
  to.value = toValue;
  inbox.value = selectedInbox?.id;
  label.value = selectedLabel?.title;
  status.value = selectedStatus?.value;
  pageIndex.value = 0;
  fetchConversations();
};

const onScopeChange = () => {
  pageIndex.value = 0;
  fetchConversations();
};

const onPageNumberChange = newPageIndex => {
  pageIndex.value = newPageIndex;
  fetchConversations();
};

const downloadReports = async () => {
  try {
    const { data } = await ReportsAPI.getConversationTrackingSpreadsheet(
      requestPayload.value
    );
    downloadBlob(
      generateFileName({
        type: 'conversation-tracking',
        to: to.value,
        extension: 'xlsx',
      }),
      data
    );
  } catch {
    useAlert(t('CONVERSATION_TRACKING_REPORTS.DOWNLOAD_FAILED'));
  }
};

const tableData = computed(() => {
  return conversations.value.map(conversation => ({
    createdAt: messageStamp(conversation.created_at, 'LLL d yyyy, h:mm a'),
    createdAgo: messageStamp(conversation.created_at, 'h:mm a'),
    lastActivityAt: messageStamp(
      conversation.last_activity_at,
      'LLL d yyyy, h:mm a'
    ),
    status: t(
      `CONVERSATION_TRACKING_REPORTS.STATUS.${conversation.status.toUpperCase()}`
    ),
    inboxName: conversation.inbox_name,
    contactName: conversation.contact_name,
    contactPhone: conversation.contact_phone,
    labels: (conversation.labels || []).join(', '),
    lastMessage: conversation.last_message,
    conversationId: conversation.id,
  }));
});

const defaultSpanRender = cellProps => {
  const value = cellProps.getValue() || '---';
  return h(
    'span',
    {
      class: 'line-clamp-3 break-words max-w-full text-n-slate-12',
      title: value,
    },
    value
  );
};

const columnHelper = createColumnHelper();

const columns = computed(() => [
  columnHelper.accessor('createdAt', {
    header: t('CONVERSATION_TRACKING_REPORTS.TABLE.HEADER.CREATED_AT'),
    width: 150,
    cell: defaultSpanRender,
  }),
  columnHelper.accessor('lastActivityAt', {
    header: t('CONVERSATION_TRACKING_REPORTS.TABLE.HEADER.LAST_ACTIVITY'),
    width: 150,
    cell: defaultSpanRender,
  }),
  columnHelper.accessor('status', {
    header: t('CONVERSATION_TRACKING_REPORTS.TABLE.HEADER.STATUS'),
    width: 100,
    cell: defaultSpanRender,
  }),
  columnHelper.accessor('inboxName', {
    header: t('CONVERSATION_TRACKING_REPORTS.TABLE.HEADER.INBOX'),
    width: 140,
    cell: defaultSpanRender,
  }),
  columnHelper.accessor('contactName', {
    header: t('CONVERSATION_TRACKING_REPORTS.TABLE.HEADER.CONTACT_NAME'),
    width: 160,
    cell: defaultSpanRender,
  }),
  columnHelper.accessor('contactPhone', {
    header: t('CONVERSATION_TRACKING_REPORTS.TABLE.HEADER.PHONE'),
    width: 140,
    cell: defaultSpanRender,
  }),
  columnHelper.accessor('labels', {
    header: t('CONVERSATION_TRACKING_REPORTS.TABLE.HEADER.LABELS'),
    width: 160,
    cell: defaultSpanRender,
  }),
  columnHelper.accessor('lastMessage', {
    header: t('CONVERSATION_TRACKING_REPORTS.TABLE.HEADER.LAST_MESSAGE'),
    width: 250,
    cell: defaultSpanRender,
  }),
  columnHelper.accessor('conversationId', {
    header: t('CONVERSATION_TRACKING_REPORTS.TABLE.HEADER.CONVERSATION_ID'),
    width: 120,
    cell: cellProps => h(ConversationCell, cellProps),
  }),
]);

const paginationParams = computed(() => ({
  pageIndex: pageIndex.value,
  pageSize: 25,
}));

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  get columns() {
    return columns.value;
  },
  manualPagination: true,
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
  get rowCount() {
    return totalCount.value;
  },
  state: {
    get pagination() {
      return paginationParams.value;
    },
  },
  onPaginationChange: updater => {
    const newPagination = updater(paginationParams.value);
    onPageNumberChange(newPagination.pageIndex);
  },
});
</script>

<template>
  <ReportHeader
    :header-title="$t('CONVERSATION_TRACKING_REPORTS.HEADER')"
    :header-description="$t('CONVERSATION_TRACKING_REPORTS.DESCRIPTION')"
  >
    <V4Button
      :label="$t('CONVERSATION_TRACKING_REPORTS.DOWNLOAD')"
      icon="i-ph-download-simple"
      size="sm"
      @click="downloadReports"
    />
  </ReportHeader>

  <div class="flex flex-col gap-4 pb-6">
    <div class="multiselect-wrap--small md:w-80">
      <multiselect
        v-model="selectedScope"
        class="no-margin"
        :option-height="24"
        :options="scopeOptions"
        :show-labels="false"
        :allow-empty="false"
        track-by="value"
        label="label"
        @update:model-value="onScopeChange"
      />
    </div>
    <ReportFilterSelector
      show-inbox-filter
      show-labels-filter
      show-status-filter
      :show-business-hours-switch="false"
      @filter-change="onFilterChange"
    />

    <div class="text-sm text-n-slate-11">
      {{
        $t('CONVERSATION_TRACKING_REPORTS.TOTAL_CONVERSATIONS', {
          count: totalCount,
        })
      }}
    </div>

    <div
      class="shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 px-6 py-5"
    >
      <div class="overflow-x-auto">
        <Table :table="table" class="w-full max-h-[calc(100vh-21.875rem)]" />
      </div>
      <div
        v-show="!tableData.length && !isLoading"
        class="h-48 flex items-center justify-center text-n-slate-12 text-sm"
      >
        {{ $t('CONVERSATION_TRACKING_REPORTS.NO_RECORDS') }}
      </div>
      <div
        v-show="isLoading"
        class="h-48 flex items-center justify-center text-n-slate-12 text-sm"
      >
        {{ $t('CONVERSATION_TRACKING_REPORTS.LOADING') }}
      </div>
      <div v-if="totalCount" class="table-pagination">
        <Pagination class="mt-2" :table="table" />
      </div>
    </div>
  </div>
</template>
